import { FilterExpression } from 'types/filters';
import { SchemaProperty } from 'types/schema';

/**
 * Resolves a `FilterExpression`'s two-coordinate `{ aspect, property }` reference to the
 * vertex/edge property key it projects onto, against the already-merged `schemaProperties`
 * (EAINT-898). Single source of truth for both graph-service Gremlin builders — do not
 * re-declare this locally in a consumer.
 *
 * - No `aspect` → `property` is returned unchanged, without scanning `schemaProperties`.
 * - `aspect` set and `property` non-empty → `property` names the target *within* the aspect;
 *   resolves to the merged property whose `aspect_system_name === aspect && name === property`.
 *   With an EMPTY inventory (nothing to verify against) it resolves to `property` itself —
 *   the same string a successful match would return — so this form behaves identically in
 *   engines that carry no inventory.
 * - `aspect` set and `property` is `''` → resolves to the aspect's primary property (the first
 *   property in `schemaProperties` stamped with that `aspect_system_name` — definition order).
 * - No match in either case → `null`, plus a structured warn naming both coordinates. Callers
 *   must FAIL CLOSED on `null` — emit a match-nothing clause, never omit the clause. Both
 *   Gremlin builders do (`.hasId('__match_nothing__')`). Omitting is not neutral: clauses are
 *   ANDed onto the traversal, so a dropped clause widens the result set.
 *
 * `log` is the caller's structured logger (`fastify.log` in both graph-service builders).
 * This runs on the request path, once per filter expression, so the warn must go through
 * the service's logging infrastructure rather than `console` — a single stored bad filter
 * would otherwise emit an unstructured line per expression per request, invisible to
 * App Insights. Optional because this lib is also consumed by the frontend bundle.
 */
export function resolveFilterPropertyName(
  filter: Pick<FilterExpression, 'aspect' | 'property'>,
  schemaProperties: SchemaProperty[],
  log?: { warn: (obj: unknown, msg: string) => void }
): string | null {
  if (!filter.aspect) return filter.property;

  const stamped = (p: SchemaProperty): boolean =>
    (p as SchemaProperty & { aspect_system_name?: string }).aspect_system_name === filter.aspect;

  const match = filter.property
    ? schemaProperties.find((p) => stamped(p) && p.name === filter.property)
    : schemaProperties.find(stamped);

  if (match) return match.name;

  // No inventory to check against is NOT the same as an inventory that disagrees.
  // In the two-coordinate form the key is self-describing — `property` names the
  // vertex/edge key directly, and a successful match would return that same string —
  // so with an empty inventory it resolves to `property` rather than excluding. This
  // is what keeps the two-coordinate form behaving identically across engines: the
  // in-memory evaluators (filterEngine, filterEvaluation) read `property` directly and
  // have no inventory at all, and the Gremlin builders that run outside a request
  // (monitoring rules, arc target resolution) supply none either.
  //
  // The bare-aspect form gets no such fallback: `property` is '' and only the
  // inventory can name the aspect's primary property, so it stays fail-closed.
  if (schemaProperties.length === 0 && filter.property) return filter.property;

  log?.warn(
    { aspect: filter.aspect, property: filter.property },
    '[resolveFilterPropertyName] Unresolvable aspect reference'
  );
  return null;
}
